import React, { useState, useMemo } from "react";
import { Box, Tooltip } from "@mui/material";
import ALoader from "../../../views/pages/manage/common/a-loader";
import ConfirmRejectionDialog from "../../../views/pages/manage/unAssigned/studentFinanceApproval/paymodeApproval/ConfirmRejectionDialog";
import AButton from "../../../views/pages/manage/common/form-fields-mui/AButton";
import CustomChip from "../../../views/pages/manage/common/customChip/CustomChip";
import { useNavigateToGivenPath } from "../../../views/pages/manage/edpOps/studentDetails/paymentSummary/service";
import { CourseDetailsPopupById } from "../../../views/pages/manage/unAssigned/studentCharges/common";
import { getStatusChipInfo } from "./common";
import { getBenificiaryDetails, rejectionHandler, updatebulkApprovalStatus } from "./utils";
import { REFUND_TYPE_MAPPING, SUB_REFUND_TYPE_MAPPING } from "./constant";
import Footer from "modules/studentTransfer/components/Footer";
import { EditHandler } from "./common";
import { BUPopover } from "views/pages/manage/unAssigned/studentFinanceApproval/epmrCancellation/constant";
import { getFormattedDate } from "views/pages/manage/common/utils/methods/commonMethods/utilityMethod";
import { getUpdatedTime } from "views/pages/manage/employeeManagement/userProfile/helper";
import { LastUpdatedDetails } from "views/pages/manage/employeeManagement/userProfile/helper";
import { formattedPrice } from "views/pages/manage/unAssigned/collectPayment/utils";
import styles from "./style.module.scss";
import ServerDataGrid from "../../../views/pages/manage/common/data-grid/ServerDataGrid";
// --- Import your new Grid ---
import { ReactComponent as EyeIcon } from "assets/img/svg/eye.svg";
import { ReactComponent as EditIcon } from "assets/img/svg/EditGryIcon.svg";
import { failureToast } from "views/pages/manage/common/utils/methods/toasterFunctions/function";

const ApprovalRequestsList = ({
  approvalRequests,
  getApprovalRequests,
  updateStatus,
  loading,
  isApprovePermission,
  paginationInfo,
  globalFilter,
  setLoader,
  dropdowns,
  userPermissions
}) => {
  const [updateStatusInfo, setUpdateStatusInfo] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [buDescriptionMap, setBuDescriptionMap] = useState({});
  const [editInfo, setEditInfo] = useState();

  // Selection State (Set for new Grid, Array for legacy logic compatibility)
  const [selectedIds, setSelectedIds] = useState(new Set());

  const navigateToGivenPath = useNavigateToGivenPath();

  // --- Logic Helpers ---
const isCheckboxEnabled = useMemo(() => {
  return (
    userPermissions?.includes("B") &&
    globalFilter?.refundTypes &&
    (globalFilter?.refundTypes ?? []).every(
      (item) => item?.value === "COMPLETION_REFUND"
    )
  );
}, [userPermissions, globalFilter?.refundTypes]);

const gridHeight = useMemo(() => {
  return isCheckboxEnabled &&
    approvalRequests.length > 0 &&
    isApprovePermission &&
    selectedIds.size !== 0
    ? "300px"
    : "400px";
}, [
  isCheckboxEnabled,
  approvalRequests.length,
  isApprovePermission,
  selectedIds.size
]);

const showBulkFooter = useMemo(() => {
  return (
    isCheckboxEnabled &&
    approvalRequests.length > 0 &&
    isApprovePermission &&
    selectedIds.size !== 0
  );
}, [
  isCheckboxEnabled,
  approvalRequests.length,
  isApprovePermission,
  selectedIds.size
]);

  const editBenificiaryHandler = async (id, row) => {
    setLoader(true);
    const res = await getBenificiaryDetails({
      refundNumber: row?.refundNumber,
    });
    setLoader(false);
    setEditInfo({ id, row: { ...row, paymentModeAccountDetailId: res?.id } });
  };

  // --- Column Definitions (Converted for ServerDataGrid) ---
  const columns = useMemo(
    () => [
      {
        key: "psid",
        label: "PSID",
        width: 120,
        sortable: true,
        render: (value) => (
          <div
            onClick={() =>
              navigateToGivenPath(`admin/edp/studentDetails/${value}`)
            }
            style={{ color: "#00B0F5", cursor: "pointer" }}
          >
            {value}
          </div>
        ),
      },
      {
        key: "refundNumber",
        label: "Refund Request No.",
        sortable: true,

        width: 150,
        render: (value) =>
          value ? (
            <Tooltip title={value}>
              <div className="ellipsis-text-container">{value}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "studentPoId",
        label: "Student PO Id",
        sortable: true,
        width: 150,
        render: (value) => (
          <div className="ellipsis-text-container">{value || "-"}</div>
        ),
      },
      {
        key: "loanPoId",
        label: "Loan PO Id",
        sortable: true,
        width: 150,
        render: (value) => (
          <div className="ellipsis-text-container">{value || "-"}</div>
        ),
      },
      {
        key: "applicationId",
        label: "Application Id",
        sortable: true,

        width: 150,
        render: (value) =>
          value ? (
            <Tooltip title={value}>
              <div className="ellipsis-text-container">{value}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "studentName",
        label: "Student Name",
        sortable: true,

        width: 160,
        render: (value) =>
          value ? (
            <Tooltip title={value}>
              <div className="ellipsis-text-container">{value}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "fatherName",
        label: "Parent Name",
        sortable: true,

        width: 130,
        render: (value) =>
          value ? (
            <Tooltip title={value}>
              <div className="ellipsis-text-container">{value}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "businessAreaValue",
        label: "BU",
        sortable: true,

        width: 120,
        render: (value, row) =>
          value ? (
            <div className="ellipsis-text-container">
              <div>
                <span>{value}</span>&nbsp;
                <span className="cursor">
                  <BUPopover
                    businessArea={row?.businessAreaValue}
                    businessAreaOptions={dropdowns?.businessAreas}
                    buDescriptionMap={buDescriptionMap}
                    setBuDescriptionMap={setBuDescriptionMap}
                  />
                </span>
              </div>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "cashierOffice",
        label: "Cashier Office",
        width: 120,
        render: (value, row) =>
          value ? (
            <div className="ellipsis-text-container">
              <div>
                <span>{value}</span>&nbsp;
                <span className="cursor">
                  <BUPopover
                    businessArea={row?.cashierOffice}
                    businessAreaOptions={dropdowns?.businessAreas}
                    buDescriptionMap={buDescriptionMap}
                    setBuDescriptionMap={setBuDescriptionMap}
                  />
                </span>
              </div>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "regularCourseId",
        label: "Regular Course ID",
        width: 130,
        render: (value) =>
          value ? (
            <div className="ellipsis-text-container">
              <span>{value}</span>&nbsp;
              <span className="cursor">
                <CourseDetailsPopupById courseId={value} cta={<EyeIcon />} />
              </span>
            </div>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "sdCourseId",
        label: "Refund Course ID",
        width: 140,
        render: (value, row) => (
          <div className="ellipsis-text-container">
            <span>{value || row.regularCourseId}</span>&nbsp;
            <span className="cursor">
              <CourseDetailsPopupById
                courseId={value || row.regularCourseId}
                cta={<EyeIcon />}
              />
            </span>
          </div>
        ),
      },
      {
        key: "termValue",
        sortable: true,

        label: "Term",
        width: 130,
        render: (value) =>
          value ? (
            <Tooltip title={value}>
              <div className="ellipsis-text-container">{value}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "refundAmount",
        label: "Refund Amount",
        sortable: true,

        width: 130,
        render: (value) =>
          value ? (
            <Tooltip title={value}>
              <div className="ellipsis-text-container">
                <div className={styles["amount"]}>{formattedPrice(value)}</div>
              </div>
            </Tooltip>
          ) : (
            <div>-</div>
          ),
      },
      {
        key: "orderType",
        label: "Refund Type",
        width: 130,
        render: (value) => {
          const display = REFUND_TYPE_MAPPING?.[value] ?? value;
          return value ? (
            <Tooltip title={display}>
              <div className="ellipsis-text-container">{display}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        key: "subOrderType",
        label: "Sub Refund Type",
        width: 130,
        render: (value) => {
          const display = SUB_REFUND_TYPE_MAPPING?.[value] ?? value;
          return value ? (
            <Tooltip title={display}>
              <div className="ellipsis-text-container">{display}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        key: "createdOn",
        sortable: true,

        label: "Initiated On",
        width: 200,
        render: (value) => {
          const display = `${getFormattedDate(value)} at ${getUpdatedTime(
            value
          )}`;
          return value ? (
            <Tooltip title={display}>
              <div className="ellipsis-text-container">{display}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        key: "updatedOn",
        sortable: true,

        label: "Last Updated On",
        width: 200,
        render: (value, row) => {
          const display = LastUpdatedDetails(value, row.updatedBy);
          return value ? (
            <Tooltip title={display}>
              <div className="ellipsis-text-container">{display}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        key: "userActionDate",
        label: "Approved On",
        width: 200,
        render: (value, row) => {
          const display = `${getFormattedDate(value)} at ${getUpdatedTime(
            value
          )}`;
          return value &&
            (row?.refundStatus?.toLowerCase() == "completed" ||
              row?.refundStatus?.toLowerCase() == "approved") ? (
            <Tooltip title={display}>
              <div className="ellipsis-text-container">{display}</div>
            </Tooltip>
          ) : (
            <div>-</div>
          );
        },
      },
      {
        key: "status",
        label: "Request Status",
        width: 180,
        pinned: true, // Replaces pinColumnLast

        render: (_, row) => (
          <CustomChip
            className="text-wrap mt-1 w-100 text-center"
            {...getStatusChipInfo(row)}
          />
        ),
      },
      {
        key: "actions",
        label: "Action",
        width: 400,
        pinned: true, // Replaces pinColumnLast
        render: (_, row) => {
          const {
            psid,
            applicationId,
            id,
            orderType,
            status,
            refundNumber,
            isL1Approver,
            isL2Approver,
            isL3Approver,
            approvalStatus,
            subOrderType,
          } = row;

          const isL1Pending = approvalStatus === "L1_PENDING";
          const isL2Pending = approvalStatus === "L2_PENDING";
          const isL3Pending = approvalStatus === "L3_PENDING";
          const actionsEnabled =
            (isL1Pending && isL1Approver) ||
            (isL2Pending && isL2Approver) ||
            (isL3Pending && isL3Approver);

          return (
            <div className="d-flex align-items-center">
              {isApprovePermission && (
                <>
                  <AButton
                    variant="danger_filled"
                    size="xs"
                    className="no-ml-btn"
                    onClick={() =>
                      rejectionHandler({
                        orderType,
                        refundNumber,
                        setUpdateStatusInfo,
                        status,
                        approvalStatus,
                        approvalAction: "REJECT",
                        isRemarkMandatory: true,
                        updatedOn: row?.updatedOn,
                        updatedBy: row?.updatedBy,
                        isUpdatedDetailRequired: true,
                        dropdownData : dropdowns,
                        studentBuId : row?.businessArea
                      })
                    }
                    disabled={!actionsEnabled}
                  >
                    Reject
                  </AButton>

                  <AButton
                    variant="primary_filled"
                    size="xs"
                    onClick={() =>
                      updateStatus({ id, approvalAction: "APPROVE", ...row })
                    }
                    disabled={!actionsEnabled}
                  >
                    Approve
                  </AButton>

                  {orderType === "MULTI_TYPE_REFUND" && (
                    <AButton
                      variant="outline"
                      textColor="#000"
                      outlineVariantColor="#CCC"
                      size="xs"
                      disabled={!actionsEnabled}
                      onClick={() => {
                        const dialogInfo = {
                          heading: "Send back for correction",
                          subHeading: `Request No : ${refundNumber}`,
                          proceedBtn: (
                            <button
                              style={{
                                backgroundColor: "#00B0F5",
                                width: "fit-content",
                                color: "#FFF",
                                border: "none",
                              }}
                            >
                              Send back for correction
                            </button>
                          ),
                          isRemarksMandatory: true,
                        };
                        rejectionHandler({
                          orderType,
                          refundNumber,
                          setUpdateStatusInfo,
                          status,
                          approvalStatus,
                          approvalAction: "RECHECK",
                          dialogInfo,
                        });
                      }}
                    >
                      Send for Correction
                    </AButton>
                  )}
                </>
              )}

              <EyeIcon
                onClick={() =>
                  navigateToGivenPath(
                    `admin/viewStudentRefundApproval?psid=${psid}&applicationId=${encodeURIComponent(
                      applicationId
                    )}&refundFlow=${encodeURIComponent(
                      "approval"
                    )}&refundNumber=${encodeURIComponent(refundNumber)}`
                  )
                }
                className="ml-2 mt-2 cursor"
              />
              {subOrderType === "COMPLETION_REFUND" && actionsEnabled && (
                <EditIcon
                  onClick={() => editBenificiaryHandler(id, row)}
                  className="ml-2 mt-2 cursor"
                />
              )}
            </div>
          );
        },
      },
    ],
    [
      isApprovePermission,
      dropdowns,
      buDescriptionMap,
      navigateToGivenPath,
      updateStatus,
      setLoader,
    ]
  );

  // --- Handlers ---
  const pagination = {
    page: (paginationInfo?.currPageNo ?? 0) + 1,
    pageSize: paginationInfo?.currPageSize || 10,
    total: paginationInfo?.totalRecords || 0,
    totalPages: paginationInfo?.totalPage || 0,
  };

  // --- Handlers ---
  const handlePageChange = (newPage) => {
    const offset = newPage - 1;

    getApprovalRequests({
      pageNo: offset,
      pageSize: pagination.pageSize,
    });
  };

  const handlePageSizeChange = (newSize) => {
    getApprovalRequests({ pageNo: 0, pageSize: newSize });
  };
  const handleSelectionChange = (newSet, selectedRowList) => {
   // Debug log to check selected IDs
    setSelectedIds(newSet);
    // Note: If you need to expose selection array to parent, call a prop here.
  };

  const bulkApprovalHandler = async () => {
    if (selectedIds.size >50) {
      failureToast("Please select up to 50 records for bulk approval.");
      return;
    }
    const selectedArray = Array.from(selectedIds);
    const rows = approvalRequests.filter(row => selectedArray.includes(row.refundNumber));
    await updatebulkApprovalStatus(rows);
    getApprovalRequests({})
  };

  return (
    <>
      {loading && <ALoader position="fixed" />}
      <ServerDataGrid
        rows={approvalRequests || []}
        loading={false} // Loading handled by ALoader above, so we don't double spin
        // Configuration
        columns={columns}
        rowKey="refundNumber" // Assuming your data has an 'id' field. If not, pass a function or unique key.
        // Pagination
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        Selection
        selectable={isCheckboxEnabled}
        selectedIds={selectedIds}
        onSelectionChange={handleSelectionChange}

        // Sorting (Optional: Pass onSortChange if you have a handler for it)
        sortConfig={sortConfig}
        onSortChange={(key, direction) => setSortConfig({ key, direction })}
        rowSelectable={(row) => (row?.approvalStatus||[]).includes("PENDING") && row?.orderType === "COMPLETION_REFUND"}
        height={gridHeight}
        isShowPageTracker = {false}
      />

      <ConfirmRejectionDialog
        updateStatusInfo={updateStatusInfo}
        setUpdateStatusInfo={setUpdateStatusInfo}
        updateStatus={updateStatus}
        dialogInfo={updateStatusInfo?.dialogInfo}
        customStyle={{ maxWidth: "424px" }}
      />

      {/* Footer for Bulk Actions */}
      {showBulkFooter && (
        <Footer updatedStyle={{zIndex: 5000}}>
          <div className='large-text d-flex align-items-center justify-content-between'>
             {selectedIds.size ? `${selectedIds.size} items selected` : 'Bulk actions'}
          </div>
          {isApprovePermission && (

              <AButton variant="primary_filled" size="xs" disabled={selectedIds.size === 0} onClick={bulkApprovalHandler}>
                Approve
              </AButton>
          )}
        </Footer>
      )}

      <EditHandler
        setOpen={setEditInfo}
        open={editInfo}
        id={editInfo?.id}
        updateStatus={updateStatus}
      />
    </>
  );
};

export default ApprovalRequestsList;
